import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ENV } from '@environments/environment.provider';
import { Observable, throwError, of, forkJoin, combineLatest, ReplaySubject } from 'rxjs';
import { filter, map, switchMap, catchError, tap, finalize, take } from 'rxjs/operators';
import { AdminService } from '@app/services/admin.service';
import { ECompanyLoginMode, PermissionsService } from '@zonar-ui/auth';
import { IUser } from '@zonar-ui/auth/lib/models/user.model';
import { IUserProfile } from '@zonar-ui/auth/lib/models/user-profile.model';
import { IUserGroupPolicy } from '@zonar-ui/auth/lib/models/user-group-policy.model';
import { UtilitiesService } from './utilities.service';
import { PaginatedResponse } from '@app/models';

@Injectable({
  providedIn: 'root'
})
export class RecipientApiHttpService {
  USERS_API_URL = this.env.coreEntityApiBase.url + '/users';
  USERPROFILES_API_URL = this.env.coreEntityApiBase.url + '/userprofiles';
  APP_ID = this.env.auth.applicationId;
  ACTIVITY_FEED_APP_ID = this.env.activityFeedBase.applicationId;

  private recipients$: ReplaySubject<Record<string, string>[]> = new ReplaySubject<Record<string, string>[]>(1);

  constructor(
    @Inject(ENV) private env: any,
    private _httpService: HttpClient,
    private _adminService: AdminService,
    private _permissionsService: PermissionsService,
    private _utilitiesService: UtilitiesService
  ) {}

  getRecipients(companyId: string): Observable<Record<string, string>[]> {
    return this._permissionsService.getIsPermissionsLoaded().pipe(
      filter((isLoaded) => isLoaded), // Ensure permissions are loaded before continuing
      switchMap(() =>
        this._utilitiesService.getCompanyLoginMode().pipe(
          switchMap((loginMode) => {
            if (loginMode === ECompanyLoginMode.GROUP_POLICY) {
              return this._getRecipientsForGroupPolicyContext(companyId);
            } else {
              return this._getRecipientsForUserProfileContext(companyId);
            }
          })
        )
      )
    );
  }

  getRecipientsFromCache(): Observable<Record<string, string>[]> {
    return this.recipients$.asObservable();
  }

  setRecipientsToCache(recipients: Record<string, string>[]) {
    this.recipients$.next(recipients);
  }

  setErrorToCache(error) {
    this.recipients$.error(error);
  }

  private _getRecipientsForUserProfileContext(companyId: string): Observable<Record<string, string>[]> {
    return this._adminService.isCompanyAdmin$.pipe(
      switchMap((isCompanyAdmin: boolean) => {
        if (isCompanyAdmin) {
          return this.getAllUsers(companyId).pipe(map((users) => ({ users })));
        } else {
          return this._adminService.isPartialAdmin$.pipe(
            switchMap((isPartialAdmin: boolean) => {
              if (isPartialAdmin) {
                const allUsers$ = this.getAllUsers(companyId);
                const userProfiles$ = this.getUserDivisions(companyId).pipe(
                  switchMap((divisions) => {
                    if (!divisions || divisions.length === 0) return of([]);
                    return this.getDivisionsUserProfiles(divisions, companyId);
                  })
                );
                return forkJoin({ allUsers: allUsers$, userProfiles: userProfiles$ });
              } else {
                return throwError(new Error('User is not authorized to view recipients.'));
              }
            })
          );
        }
      }),
      map((result: { users: IUser[] } | { allUsers: IUser[]; userProfiles: IUserProfile[] }) => {
        let users: IUser[] = [];
        if ('users' in result) {
          users = result.users;
        } else if ('allUsers' in result && 'userProfiles' in result) {
          // filter out all the userProfiles whose divisions is a an empty array
          result.userProfiles = result.userProfiles.filter((userProfile) => userProfile.divisions.length > 0);
          users = this.mapUserProfilesToUsers(result.allUsers, result.userProfiles);
        }
        const recipients = users.map((user: IUser) => {
          const userId = user.id!;
          const name = this._capitalizeFullName(`${user.firstName} ${user.lastName}`);
          const title = `${name} (${user.email})`;
          return {
            title: title,
            value: userId,
            tooltipContext: title
          };
        });
        recipients.sort((a, b) => a.title.localeCompare(b.title));
        return recipients;
      }),
      catchError((error) => {
        return throwError(new Error('Failed to load recipients'));
      })
    );
  }

  private _getRecipientsForGroupPolicyContext(companyId: string): Observable<Record<string, string>[]> {
    return this._getUserGroupPoliciesFilteredByCompanyId(companyId).pipe(
      switchMap((response) => {
        const uniqueTenantIdsSet = new Set();
        response.forEach((policy) => {
          uniqueTenantIdsSet.add(policy.tenant.id);
        });

        const uniqueTenantIdsArray = Array.from(uniqueTenantIdsSet);
        let uniqueTenantIdsObservables = [];
        uniqueTenantIdsArray.forEach((id) => {
          uniqueTenantIdsObservables.push(this._getRecipientsByTenant(id));
        });
        return combineLatest(...uniqueTenantIdsObservables).pipe(
          map((val) => {
            const output = this._utilitiesService.deduplicateArrayByValue(val.flat(), 'value');
            return output;
          }),
          catchError((err) => {
            throw new Error(`Unable to get recipients for Group Policy context: ${err.message}`);
          })
        );
      })
    );
  }

  private _getUserGroupPoliciesFilteredByCompanyId(companyId: string): Observable<Array<IUserGroupPolicy>> {
    return this._permissionsService.getUserGroupPolicies().pipe(
      filter((x) => !!x),
      map((userPolicies) => userPolicies.filter((policy) => policy.policy.companyId === companyId)),
    );
  }

  private _getRecipientsByTenant(tenantId) {
    const groupsApiUrl = `${this.env.coreEntityApiBase.url}/groups?tenantId=${tenantId}`;
    return this._utilitiesService.getAllPages(groupsApiUrl).pipe(
      map((response: Array<any>) => {
        const membersArrays = [];
        response.forEach((group) => {
          membersArrays.push(group.members);
        });
        const flattenedMembers = membersArrays.flat();
        const formattedRecipients = flattenedMembers.map((member) => {
        const title = `${member.firstName} ${member.lastName} (${member.email})`;
          return {
            title: title,
            value: member.id,
            tooltipContext: title
          };
        });
        return formattedRecipients;
      })
    );
  }

  private mapUserProfilesToUsers(allUsers: IUser[], userProfiles: IUserProfile[]): IUser[] {
    const allUsersMap = new Map<string, IUser>();
    allUsers.forEach((user: IUser) => {
      if (user.id) allUsersMap.set(user.id, user);
    });
    const uniqueUserIds = new Set<string>();
    userProfiles.forEach((userProfile: IUserProfile) => {
      const userId = userProfile.userId;
      if (userId) uniqueUserIds.add(userId);
    });
    return Array.from(uniqueUserIds)
      .map((userId) => allUsersMap.get(userId))
      .filter((user): user is IUser => user !== undefined);
  }

  private getUserDivisions(companyId: string): Observable<string[]> {
    return this._permissionsService.getUser().pipe(
      take(1), // Ensure the observable completes after emitting one value
      switchMap((user: IUser) => {
        const userId = user.id!;
        const url = `${this.USERS_API_URL}/${userId}/companies/${companyId}/divisions?applicationId=${this.ACTIVITY_FEED_APP_ID}&allChildren=true`;
        return this._httpService.get<{ divisions: string[] }>(url).pipe(
          map((response) => response.divisions || [])
        );
      }),
      catchError((error) => {
        return of([]); // Return empty array on error
      })
    );
  }

  private getAllUsers(companyId: string): Observable<IUser[]> {
    const perPage = 1000;
    const userUrl = `${this.USERS_API_URL}?companyId=${companyId}&status=ACTIVE`;

    // Step 1: Perform a HEAD request to get the total count from the headers
    return this._httpService.head(userUrl, { observe: 'response' }).pipe(
      switchMap((response) => {
        const totalCount = Number(response.headers.get('x-total-count')); // Get the total count from the header
        const totalPages = Math.ceil(totalCount / perPage); // Calculate the total number of pages

        if (totalCount === 0) {
          return of([]); // Return empty array when no users found
        }

        // Step 2: Create an array of observables for each page
        const pageRequests: Observable<PaginatedResponse<IUser>>[] = [];
        for (let page = 1; page <= totalPages; page++) {
          const paginatedUrl = `${this.USERS_API_URL}?companyId=${companyId}&status=ACTIVE`;
          pageRequests.push(this._utilitiesService.getPaginatedData<IUser>(paginatedUrl, page, perPage));
        }

        // Step 3: Use forkJoin to run all the requests in parallel
        return forkJoin(pageRequests);
      }),
      // Step 4: Merge the results from all the pages into a single array
      map((results: PaginatedResponse<IUser>[]) => {
        return results.reduce((acc, result) => acc.concat(result.items), [] as IUser[]);
      }),
      catchError((error) => {
        return throwError(() => new Error('Error fetching all users (Parallel)'));
      })
    );
  }

  private getDivisionsUserProfiles(divisions: string[], companyId: string): Observable<IUserProfile[]> {
    if (!divisions || divisions.length === 0) {
      return of([]);
    }

    // Create observables for each division by making individual API calls for each division ID
    const observables = divisions.map((divisionId) => {
      // Build the API URL with a single division ID (implicitDivisionId)
      const userProfilesUrl = `${this.USERPROFILES_API_URL}?companyId=${companyId}&implicitDivisionId=${divisionId}`;

      // Fetch all pages for the division
      return this._utilitiesService.getAllPages<IUserProfile>(userProfilesUrl);
    });

    // Fetch all division-related user profiles in parallel
    return forkJoin(observables).pipe(
      map((results) => {
        // Flatten the results into a single array
        return results.reduce((acc, val) => acc.concat(val), []);
      })
    );
  }

  private _capitalizeFirstLetter(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  }

  private _capitalizeFullName(name: string): string {
    return name.split(' ').map(this._capitalizeFirstLetter).join(' ');
  }
}
